import { DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CreateUserRequest,
  UpdateUserRequest,
  UserByIdResponse,
  UserClient,
} from '../../../core/openapi/bff-admin-api';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, map, Observable, tap, throwError } from 'rxjs';
import { outputError } from '../../../shared/utils/output-error.function';
import { LoadingState } from '../../../shared/types/loading.type';

@Injectable()
export class UserManagementService {
  private readonly userClient = inject(UserClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _loadingState: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );

  public readonly loadingState: Signal<LoadingState> = this._loadingState.asReadonly();

  public createUser(request: CreateUserRequest): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.userClient.v1UserPost(request).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.users.modal.toast.user-created', {
            name: `${request.firstName} ${request.lastName}`,
          }),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.users.modal.toast.user-creation-failed',
            {
              name: `${request.firstName} ${request.lastName}`,
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public loadUserById(userId: string): Observable<UserByIdResponse> {
    if (userId.length === 0) {
      return throwError(() => new Error('No user id provided'));
    }

    this._loadingState.set(LoadingState.LOADING);

    return this.userClient.v1UserIdGet(userId).pipe(
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.users.modal.toast.user-reading-failed',
            {
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public updateUser(request: UpdateUserRequest): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.userClient.v1UserPut(request).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.users.modal.toast.user-updated', {
            name: `${request.firstName} ${request.lastName}`,
          }),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.users.modal.toast.user-update-failed',
            {
              name: `${request.firstName} ${request.lastName}`,
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public deleteUser(id: string, name: string): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.userClient.v1UserIdDelete(id).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.users.modal.toast.user-removed', {
            name,
          }),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.users.modal.toast.user-deletion-failed',
            {
              name,
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public blockUser(id: string, name: string): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.userClient.v1UserIdBlockPut(id).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.users.modal.toast.user-blocked', {
            name,
          }),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.users.modal.toast.user-blocking-failed',
            {
              name,
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public unblockUser(id: string, name: string): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.userClient.v1UserIdUnblockPut(id).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant('monitoring.users.modal.toast.user-unblocked', {
            name,
          }),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.users.modal.toast.user-unblocking-failed',
            {
              name,
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  public resetPassword(id: string, name: string): Observable<void> {
    this._loadingState.set(LoadingState.LOADING);

    return this.userClient.v1UserIdResetPasswordPost(id).pipe(
      map((): void => undefined),
      tap(() => {
        this.toastService.success({
          message: this.translateService.instant(
            'monitoring.users.modal.toast.user-password-reset',
            {
              name,
            },
          ),
        });
      }),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.users.modal.toast.user-password-reset-failed',
            {
              name,
              error: outputError(error, this.translateService.instant('errors.unknown')),
            },
          ),
        });
        return throwError(() => error);
      }),
      finalize(() => this._loadingState.set(LoadingState.READY)),
      takeUntilDestroyed(this.destroyRef),
    );
  }
}
